import { Component, inject, OnChanges, OnInit, signal } from '@angular/core';
import { TiktokSparkAdsService } from '../../../../services/tiktok-spark-ads/tiktok-spark-ads.service';
import { CreateSparkCampaignDto, CreateSparkCampaignResponseDto, SparkAdBudgetMode } from '../../../../common/general-types/tiktok-spark-ads.dto';
import { ManageBriefTabsService } from '../../service/manage-brief-tabs/manage-brief-tabs.service';
import { AlertService } from '../../../../atoms/alert/alert.service';
import { AdvertisingObjectiveParent } from './molecules/advertising-objective-parent/advertising-objective-parent';
import { AdvertisingSettings } from './molecules/advertising-settings/advertising-settings';
import { CreateSparkCampaign } from './service/create-spark-campaign/create-spark-campaign';
import { AdvertisingAdvertiserId } from './molecules/advertising-advertiser-id/advertising-advertiser-id';
import { LoadingOverlayComponent } from '@/app/atoms/loading-overlay/loading-overlay.component';
import { Router } from '@angular/router';
import { SparkAdGroupsTableComponent } from './atoms/spark-ad-groups-table/spark-ad-groups-table';

@Component({
  selector: 'mbt-boost-tiktok-v2',
  standalone: true,
  imports: [AdvertisingObjectiveParent, AdvertisingSettings, AdvertisingAdvertiserId,
    LoadingOverlayComponent,
    SparkAdGroupsTableComponent,
  ],
  templateUrl: './boost-tiktok-v2.html',
  styleUrl: './boost-tiktok-v2.css',
})
export class BoostTiktokV2Component implements OnInit {


  noSparkCampaign = signal<boolean | undefined>(undefined);

  selectedAdvertiserId = signal<string | undefined>(undefined);
  objectiveType = signal<'VIDEO_VIEWS' | 'APP_PROMOTION' | 'ENGAGEMENT' | 'LEAD_GENERATION' | 'REACH' | 'TRAFFIC' | 'WEB_CONVERSIONS' | undefined>(undefined);
  campaignName = signal<string | undefined>(undefined);
  budget = signal<number | undefined>(undefined);
  budgetMode = signal<SparkAdBudgetMode | undefined>(undefined);

  creatingSparkCampaign = signal<boolean>(false);

  /** Services */
  private router = inject(Router);
  private tiktokSparkAdsService = inject(TiktokSparkAdsService);
  private manageBriefTabsService = inject(ManageBriefTabsService);
  private alertService = inject(AlertService);
  public createSparkCampaignService = inject(CreateSparkCampaign);
  /** Services - END */


  ngOnInit(): void {
    console.log('Boost V2 - ngOnInit() ---> ');
    console.log('Brief Details: ', this.manageBriefTabsService.focussedBrief());

    this.getAssociatedCampaign();
  }

  getAssociatedCampaign() {
    console.log('getAssociatedCampaign() ---> ');
    const brief = this.manageBriefTabsService.focussedBrief();
    if (!brief?.id) {
      return;
    }
    this.tiktokSparkAdsService.getSparkCampaignForBrief(brief.id).subscribe({
      next: (campaign: CreateSparkCampaignResponseDto | null) => {
        if (!campaign) {
          this.noSparkCampaign.set(true);
          return;
        }
        this.createSparkCampaignService.currentSparkCampaign.set(campaign);
        this.noSparkCampaign.set(false);
      },
      error: (err) => {
        console.error('getAssociatedCampaign() ---> error: ', err);
        this.noSparkCampaign.set(true);
      },
    });
  }


  async createSparkCampaign() {
    console.log('createSparkCampaign() ---> ');

    this.selectedAdvertiserId.set(this.createSparkCampaignService.selectedAdvertiserAccountId()!);
    this.objectiveType.set(this.createSparkCampaignService.selectedAdvertisingObjective()?.api_value! as 'VIDEO_VIEWS' | 'APP_PROMOTION' | 'ENGAGEMENT' | 'LEAD_GENERATION' | 'REACH' | 'TRAFFIC' | 'WEB_CONVERSIONS');
    this.campaignName.set(this.createSparkCampaignService.campaignName()!);
    this.budget.set(this.createSparkCampaignService.budget()!);
    this.budgetMode.set(this.createSparkCampaignService.selectedBudgetMode()!);

    let isValid = this.checkCampaignValidity();

    if (!isValid) {
      await this.alertService.alert({
        title: 'Could not create TikTok Spark Campaign',
        message: 'Please fill in all the fields to create a campaign.',
        confirmText: 'OK',
        cancelText: 'Cancel',
        type: 'alert',
        icon: 'info',
      });
    } else {

      console.log('createSparkCampaign() ---> isValid: ', isValid);
      const brief = this.manageBriefTabsService.focussedBrief();
      if (!brief?.id || !brief.shoutBrief?.id) {
        return;
      }
      const newCampaign: CreateSparkCampaignDto = {
        briefId: brief.id,
        shoutBriefId: brief.shoutBrief.id,
        ttAdvertiserId: this.selectedAdvertiserId()!,
        objectiveType: this.objectiveType()!,
        campaignName: this.campaignName()!,
        budget: this.budget()!,
        budgetMode: this.budgetMode()!,
        budgetOptimizeOn: this.createSparkCampaignService.budgetOptimizeOn(),
      };

      this.creatingSparkCampaign.set(true);

      this.tiktokSparkAdsService.createCampaign(newCampaign).subscribe({
        next: (campaign: CreateSparkCampaignResponseDto) => {
          console.log('createSparkCampaign() ---> campaign: ', campaign);
          this.createSparkCampaignService.currentSparkCampaign.set(campaign);
          this.noSparkCampaign.set(false);
          this.creatingSparkCampaign.set(false);
        },
        error: (err) => {
          console.error('createSparkCampaign() ---> error: ', err);
          this.noSparkCampaign.set(true);
          this.creatingSparkCampaign.set(false);
        },
      });
    }
  }

  checkCampaignValidity(): boolean {
    return this.selectedAdvertiserId() !== undefined &&
      this.objectiveType() !== undefined &&
      this.campaignName() !== undefined &&
      this.budget() !== undefined &&
      this.budgetMode() !== undefined;
  }

  goToAdGroupCreate() {
    const briefId = this.manageBriefTabsService.focussedBrief()?.id;
    if (this.createSparkCampaignService.currentSparkCampaign() !== undefined && briefId) {
      this.router.navigate(['/ad-group-create', briefId, this.createSparkCampaignService.currentSparkCampaign()!.id!]);
    }
  }

  goToCreateAd() {
    const briefId = this.manageBriefTabsService.focussedBrief()?.id;
    if (this.createSparkCampaignService.currentSparkCampaign() !== undefined && this.createSparkCampaignService.currentSparkAdGroup() !== undefined && briefId) {
      this.router.navigate(['/ad-create', briefId, this.createSparkCampaignService.currentSparkCampaign()!.id!, this.createSparkCampaignService.currentSparkAdGroup()!.id!]);
    }
  }

  clearSelectedSparkAdGroup(): void {
    this.createSparkCampaignService.currentSparkAdGroup.set(undefined);
  }

  async createSparkAdGroup() {
    console.log('createSparkAdGroup() ---> ');
    // this.tiktokSparkAdsService.createSparkAdGroup().subscribe({
    //   next: (adGroup: any) => {
    //     console.log('createSparkAdGroup() ---> adGroup: ', adGroup);

    //   },
    //   error: (err) => {
    //     console.error('createSparkAdGroup() ---> error: ', err);

    //   },
    // });
  }


  formatSparkDate(iso: string | undefined): string {
    if (!iso) {
      return '—';
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      return iso;
    }
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  }

  formatSparkBudget(amount: number | undefined): string {
    if (amount === undefined || amount === null) {
      return '—';
    }
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(amount);
  }

  sparkBudgetModeLabel(mode: string | undefined): string {
    if (!mode) {
      return '—';
    }
    switch (mode) {
      case 'BUDGET_MODE_DAY':
        return 'Daily';
      case 'BUDGET_MODE_TOTAL':
        return 'Total';
      case 'BUDGET_MODE_INFINITE':
        return 'Unlimited';
      default:
        return mode.replace(/^BUDGET_MODE_/, '').split('_').join(' ');
    }
  }

  sparkOperationStatusIsEnabled(status: string | undefined): boolean {
    if (!status) {
      return false;
    }
    const s = status.toUpperCase();
    return s === 'ENABLE' || s.includes('ACTIVE');
  }

  sparkOperationStatusLabel(status: string | undefined): string {
    if (!status) return '—';
    const s = status.toUpperCase();
    if (s === 'ENABLE' || s.includes('ACTIVE')) return 'Active';
    if (s === 'DISABLE' || s.includes('DELETE')) return 'Disabled';
    if (s.includes('PAUSE') || s.includes('FROZEN')) return 'Paused';
    return status
      .replace(/^ADGROUP_STATUS_/, '')
      .replace(/^CAMPAIGN_STATUS_/, '')
      .split('_')
      .map(w => w.charAt(0) + w.slice(1).toLowerCase())
      .join(' ');
  }

  formatObjectiveType(type: string | undefined): string {
    if (!type) return '—';
    return type.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
  }

  /** TikTok API enum-style strings (optimization goal, placements, etc.) */
  formatAdGroupApiValue(raw: string | null | undefined): string {
    if (!raw) {
      return '—';
    }
    return raw
      .replace(/^(ADGROUP_|BID_|PLACEMENT_|GENDER_)/i, '')
      .split('_')
      .filter(Boolean)
      .map(w => w.charAt(0) + w.slice(1).toLowerCase())
      .join(' ');
  }

  sparkOperationStatusClass(status: string | undefined): string {
    if (!status) {
      return 'bg-gray-100 text-gray-800 ring-1 ring-gray-500/10 dark:bg-white/10 dark:text-gray-200 dark:ring-white/10';
    }
    const s = status.toUpperCase();
    if (s === 'ENABLE' || s.includes('ACTIVE')) {
      return 'bg-emerald-500/15 text-emerald-900 ring-1 ring-emerald-600/20 dark:text-emerald-100 dark:ring-emerald-400/25';
    }
    if (s === 'DISABLE' || s.includes('DELETE')) {
      return 'bg-gray-100 text-gray-800 ring-1 ring-gray-500/10 dark:bg-white/10 dark:text-gray-200 dark:ring-white/10';
    }
    if (s.includes('PAUSE') || s.includes('FROZEN')) {
      return 'bg-amber-500/15 text-amber-950 ring-1 ring-amber-600/25 dark:text-amber-100 dark:ring-amber-400/30';
    }
    return 'bg-primary/10 text-accent ring-1 ring-primary/25 dark:ring-primary/30';
  }
}
